import { useState, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import {
  ArrowLeft, Briefcase, Loader2, AlertCircle, Send, Download,
  FileText, CheckCircle, XCircle, MessageSquare, Plus, User, Phone, Mail,
  AlertTriangle, Calendar, Upload, Wrench, ClipboardList, Star,
  Receipt, Ban, Link2, TicketCheck, GitFork,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface DevisLigne { description: string; quantite: number; prixUnitaire: number; total: number; }
interface DevisDetail {
  id: string; version: number; lignes: DevisLigne[];
  montantHt: string | null; tauxTva: string | null; montantTtc: string | null;
  validiteJours: number | null; conditions: string | null;
  envoye: boolean | null; envoyeAt: string | null;
  accepte: boolean | null; accepteAt: string | null; accepteNote: string | null;
  acceptationIp: string | null; acceptationUserId: string | null;
  estAvenant: boolean | null; avenantDe: string | null;
  createdAt: string;
}
interface Message { id: string; auteur: string; auteurType: string; contenu: string; createdAt: string; }
interface PjSigned { name: string; size: number; url: string; }
interface Technician { id: string; prenom: string; nom: string; isActive?: boolean; }
interface TicketOption { id: string; ticketNumber: string; }
interface PrestationDetail {
  id: string; numero: string; statut: string; typePrestation: string; titre: string;
  description: string | null; niveauUrgence: string | null;
  siteNom: string | null; siteAdresse: string | null;
  clientFinalNom: string | null; clientFinalAdresse: string | null;
  contactTerrainNom: string | null; contactTerrainTel: string | null; contactTerrainEmail: string | null;
  modeleRobot: string | null; numeroSerie: string | null;
  descriptionPanne: string | null; piecesSuspectes: string[];
  dateIntervention: string | null; dateInterventionFin: string | null;
  noteInterne: string | null; assigneA: string | null; missionId: string | null; ticketId: string | null;
  piecesJointesSignees: PjSigned[]; resellerName: string | null;
  devis: DevisDetail[]; messages: Message[];
  createdAt: string;
  // Phase 2
  notation: number | null;
  notationCommentaire: string | null;
  notationAt: string | null;
  factureUrl: string | null;
  factureNumeroZoho: string | null;
  factureMontant: string | null;
  factureAt: string | null;
  motifAnnulation: string | null;
}

const STATUTS = ["demande_recue", "devis_envoye", "devis_accepte", "devis_refuse", "intervention_planifiee", "intervention_realisee", "facture", "clos", "annule"] as const;
const STATUT_LABELS: Record<string, string> = {
  demande_recue: "Demande reçue", devis_envoye: "Devis envoyé", devis_accepte: "Devis accepté",
  devis_refuse: "Devis refusé", intervention_planifiee: "Planifiée", intervention_realisee: "Réalisée",
  facture: "Facturée", clos: "Clôturée", annule: "Annulée",
};
const STATUT_COLORS: Record<string, string> = {
  demande_recue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  devis_envoye: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  devis_accepte: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  devis_refuse: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  intervention_planifiee: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  intervention_realisee: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  facture: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  clos: "bg-secondary text-muted-foreground", annule: "bg-secondary text-muted-foreground",
};
const URGENCE_LABELS: Record<string, string> = { faible: "🟢 Faible", normal: "🔵 Normal", urgent: "🟠 Urgent", critique: "🔴 Critique" };
const URGENCE_COLORS: Record<string, string> = {
  faible: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400",
  normal: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
  urgent: "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400",
  critique: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
};

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

const EMPTY_LIGNE: DevisLigne = { description: "", quantite: 1, prixUnitaire: 0, total: 0 };

function StarDisplay({ value }: { value: number }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`w-4 h-4 ${value >= i ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
      ))}
    </div>
  );
}

export default function SousTraitanceDetail() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const id = params.id;

  const [tab, setTab] = useState<"detail" | "devis" | "messages" | "planifier" | "rapport" | "facturation" | "annuler">("detail");
  const [msgContent, setMsgContent] = useState("");
  const [showDevisForm, setShowDevisForm] = useState(false);
  const [showAvenantForm, setShowAvenantForm] = useState(false);
  const [lignes, setLignes] = useState<DevisLigne[]>([{ ...EMPTY_LIGNE }]);
  const [tauxTva, setTauxTva] = useState("20");
  const [validiteJours, setValiditeJours] = useState("30");
  const [conditions, setConditions] = useState("");

  // Planifier form state
  const [planDateDebut, setPlanDateDebut] = useState("");
  const [planDateFin, setPlanDateFin] = useState("");
  const [planTechnicien, setPlanTechnicien] = useState("");
  const [planNote, setPlanNote] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  // Rapport form state
  const [rapportFiles, setRapportFiles] = useState<File[]>([]);
  const [rapportCommentaire, setRapportCommentaire] = useState("");
  const [rapportLoading, setRapportLoading] = useState(false);
  const [rapportError, setRapportError] = useState<string | null>(null);
  const rapportInputRef = useRef<HTMLInputElement>(null);

  // Phase 2 — Facturation
  const [factureFile, setFactureFile] = useState<File | null>(null);
  const [factureNumeroZoho, setFactureNumeroZoho] = useState("");
  const [factureMontant, setFactureMontant] = useState("");
  const [factureLoading, setFactureLoading] = useState(false);
  const [factureError, setFactureError] = useState<string | null>(null);
  const factureInputRef = useRef<HTMLInputElement>(null);

  // Phase 2 — Annulation
  const [motifAnnulation, setMotifAnnulation] = useState("");
  const [annulationLoading, setAnnulationLoading] = useState(false);
  const [annulationError, setAnnulationError] = useState<string | null>(null);

  // Phase 2 — Lier ticket
  const [showLierTicket, setShowLierTicket] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [lierTicketLoading, setLierTicketLoading] = useState(false);

  const { data: prestation, isLoading, error } = useQuery<PrestationDetail>({
    queryKey: ["/api/prestations", id],
    queryFn: () => authFetch(`/api/prestations/${id}`).then(r => r.json()),
    enabled: !!id,
  });

  const { data: technicians } = useQuery<Technician[]>({
    queryKey: ["/api/profiles/technicians"],
    queryFn: () => authFetch("/api/profiles?role=technician").then(r => r.json()),
  });

  const { data: tickets } = useQuery<TicketOption[]>({
    queryKey: ["/api/tickets"],
    queryFn: () => authFetch("/api/tickets").then(r => r.json()).then((rows: any[]) => rows.map(t => ({ id: t.id, ticketNumber: t.ticketNumber }))),
  });

  const patchMutation = useMutation({
    mutationFn: (body: Record<string, any>) =>
      authFetch(`/api/prestations/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(r => r.json()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/prestations", id] }); qc.invalidateQueries({ queryKey: ["/api/prestations"] }); toast({ title: "Mis à jour" }); },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const msgMutation = useMutation({
    mutationFn: (contenu: string) =>
      authFetch(`/api/prestations/${id}/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ contenu }) }).then(r => r.json()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/prestations", id] }); setMsgContent(""); },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const devisMutation = useMutation({
    mutationFn: (body: Record<string, any>) =>
      authFetch(`/api/prestations/${id}/devis`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(r => r.json()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/prestations", id] }); setShowDevisForm(false); toast({ title: "Devis créé" }); },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const avenantMutation = useMutation({
    mutationFn: (body: Record<string, any>) =>
      authFetch(`/api/prestations/${id}/avenant`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(r => r.json()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/prestations", id] }); setShowAvenantForm(false); setLignes([{ ...EMPTY_LIGNE }]); toast({ title: "Avenant créé" }); },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const cloreMutation = useMutation({
    mutationFn: () => authFetch(`/api/prestations/${id}/clore`, { method: "POST" }).then(r => r.json()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/prestations", id] }); qc.invalidateQueries({ queryKey: ["/api/prestations"] }); toast({ title: "Prestation clôturée" }); },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const envoyerMutation = useMutation({
    mutationFn: () => authFetch(`/api/prestations/${id}/devis/envoyer`, { method: "POST" }).then(r => r.json()),
    onSuccess: (data) => { qc.invalidateQueries({ queryKey: ["/api/prestations", id] }); toast({ title: "Devis envoyé", description: `Email à ${data.sentTo}` }); },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  function updateLigne(i: number, key: keyof DevisLigne, val: string) {
    setLignes(ls => ls.map((l, j) => {
      if (j !== i) return l;
      const u = { ...l, [key]: key === "description" ? val : Number(val) };
      u.total = u.quantite * u.prixUnitaire;
      return u;
    }));
  }

  const montantHt = lignes.reduce((s, l) => s + l.total, 0);
  const montantTtc = montantHt * (1 + Number(tauxTva) / 100);

  async function handlePlanifier() {
    if (!planDateDebut) { setPlanError("La date de début est requise."); return; }
    setPlanLoading(true); setPlanError(null);
    try {
      const res = await authFetch(`/api/prestations/${id}/planifier`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dateIntervention: planDateDebut, dateInterventionFin: planDateFin || undefined, technicienId: planTechnicien || undefined, noteIntervention: planNote || undefined }),
      });
      if (!res.ok) { const err = await res.json(); setPlanError(err.message || "Erreur."); return; }
      await qc.invalidateQueries({ queryKey: ["/api/prestations", id] });
      await qc.invalidateQueries({ queryKey: ["/api/prestations"] });
      toast({ title: "Intervention planifiée", description: "Mission créée et partenaire notifié." });
      setTab("detail");
    } catch { setPlanError("Erreur réseau."); }
    finally { setPlanLoading(false); }
  }

  async function handleRapport() {
    if (rapportFiles.length === 0 && !rapportCommentaire.trim()) { setRapportError("Ajoutez un fichier ou un commentaire."); return; }
    setRapportLoading(true); setRapportError(null);
    try {
      const fd = new FormData();
      rapportFiles.forEach(f => fd.append("rapport", f));
      if (rapportCommentaire.trim()) fd.append("commentaireRapport", rapportCommentaire.trim());
      const res = await authFetch(`/api/prestations/${id}/rapport`, { method: "POST", body: fd });
      if (!res.ok) { const err = await res.json(); setRapportError(err.message || "Erreur."); return; }
      await qc.invalidateQueries({ queryKey: ["/api/prestations", id] });
      await qc.invalidateQueries({ queryKey: ["/api/prestations"] });
      toast({ title: "Rapport déposé", description: "Partenaire notifié." });
      setRapportFiles([]); setRapportCommentaire("");
      setTab("detail");
    } catch { setRapportError("Erreur réseau."); }
    finally { setRapportLoading(false); }
  }

  async function handleFacture() {
    setFactureLoading(true); setFactureError(null);
    try {
      const fd = new FormData();
      if (factureFile) fd.append("facture", factureFile);
      if (factureNumeroZoho.trim()) fd.append("numeroZoho", factureNumeroZoho.trim());
      if (factureMontant.trim()) fd.append("montant", factureMontant.trim());
      const res = await authFetch(`/api/prestations/${id}/facture`, { method: "POST", body: fd });
      if (!res.ok) { const err = await res.json(); setFactureError(err.message || "Erreur."); return; }
      await qc.invalidateQueries({ queryKey: ["/api/prestations", id] });
      await qc.invalidateQueries({ queryKey: ["/api/prestations"] });
      toast({ title: "Facture enregistrée", description: "Le partenaire a été notifié." });
      setFactureFile(null); setFactureNumeroZoho(""); setFactureMontant("");
      setTab("detail");
    } catch { setFactureError("Erreur réseau."); }
    finally { setFactureLoading(false); }
  }

  async function handleAnnuler() {
    if (!motifAnnulation.trim()) { setAnnulationError("Un motif est obligatoire."); return; }
    setAnnulationLoading(true); setAnnulationError(null);
    try {
      const res = await authFetch(`/api/prestations/${id}/annuler`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ motif: motifAnnulation.trim() }),
      });
      if (!res.ok) { const err = await res.json(); setAnnulationError(err.message || "Erreur."); return; }
      await qc.invalidateQueries({ queryKey: ["/api/prestations", id] });
      await qc.invalidateQueries({ queryKey: ["/api/prestations"] });
      toast({ title: "Prestation annulée" });
      setTab("detail");
    } catch { setAnnulationError("Erreur réseau."); }
    finally { setAnnulationLoading(false); }
  }

  async function handleLierTicket() {
    setLierTicketLoading(true);
    try {
      const res = await authFetch(`/api/prestations/${id}/lier-ticket`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticketId: selectedTicketId || null }),
      });
      if (!res.ok) throw new Error("Erreur");
      await qc.invalidateQueries({ queryKey: ["/api/prestations", id] });
      setShowLierTicket(false);
      toast({ title: selectedTicketId ? "Ticket lié" : "Liaison supprimée" });
    } catch (e: any) { toast({ title: "Erreur", description: e.message, variant: "destructive" }); }
    finally { setLierTicketLoading(false); }
  }

  async function handleDownloadFacture() {
    try {
      const res = await authFetch(`/api/prestations/${id}/facture`);
      if (!res.ok) return;
      const { url } = await res.json();
      window.open(url, "_blank");
    } catch {}
  }

  if (isLoading) return (
    <div className="flex items-center justify-center h-full py-20">
      <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
    </div>
  );

  if (error || !prestation) return (
    <div className="p-6 space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate("/sous-traitance")} className="gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Retour
      </Button>
      <div className="flex items-center gap-2 text-destructive text-sm p-4 bg-destructive/10 rounded-xl">
        <AlertCircle className="w-4 h-4" /> Prestation introuvable.
      </div>
    </div>
  );

  const latestDevis = prestation.devis?.[0] || null;
  const canPlanifier = ["devis_accepte", "demande_recue"].includes(prestation.statut);
  const canRapport = prestation.statut === "intervention_planifiee";
  const canFacture = ["intervention_realisee", "facture"].includes(prestation.statut);
  const canClore = prestation.statut === "facture";
  const canAnnuler = !["clos", "annule"].includes(prestation.statut);
  const canAvenant = latestDevis?.accepte === true && !["clos", "annule"].includes(prestation.statut);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/60 shrink-0 gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/sous-traitance")} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-muted-foreground">{prestation.numero}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[prestation.statut] || "bg-secondary text-muted-foreground"}`}>
                {STATUT_LABELS[prestation.statut] || prestation.statut}
              </span>
              {prestation.niveauUrgence && prestation.niveauUrgence !== "normal" && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCE_COLORS[prestation.niveauUrgence] || ""}`}>
                  {URGENCE_LABELS[prestation.niveauUrgence]}
                </span>
              )}
              {prestation.resellerName && <span className="text-xs text-muted-foreground">{prestation.resellerName}</span>}
            </div>
            <h1 className="text-base font-semibold text-foreground truncate mt-0.5">{prestation.titre}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {latestDevis && !latestDevis.envoye && (
            <Button size="sm" variant="outline" onClick={() => envoyerMutation.mutate()} disabled={envoyerMutation.isPending}>
              {envoyerMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Envoyer le devis
            </Button>
          )}
          {latestDevis && (
            <Button size="sm" variant="outline" onClick={() => window.open(`/api/prestations/${id}/devis/pdf`, "_blank")} data-testid="btn-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          )}
          {canFacture && (
            <Button size="sm" variant="outline" onClick={() => setTab("facturation")} className="gap-1.5" data-testid="btn-facturation">
              <Receipt className="w-3.5 h-3.5" /> Facture
            </Button>
          )}
          {canClore && (
            <Button size="sm" variant="outline" onClick={() => cloreMutation.mutate()} disabled={cloreMutation.isPending} className="gap-1.5">
              {cloreMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Clôturer
            </Button>
          )}
          {canPlanifier && (
            <Button size="sm" onClick={() => setTab("planifier")} className="gap-1.5" data-testid="btn-planifier">
              <Calendar className="w-3.5 h-3.5" /> Planifier
            </Button>
          )}
          {canRapport && (
            <Button size="sm" variant="outline" onClick={() => setTab("rapport")} className="gap-1.5" data-testid="btn-rapport">
              <ClipboardList className="w-3.5 h-3.5" /> Déposer rapport
            </Button>
          )}
          {canAnnuler && (
            <Button size="sm" variant="ghost" onClick={() => setTab("annuler")} className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="btn-annuler">
              <Ban className="w-3.5 h-3.5" /> Annuler
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border/60 px-6 shrink-0 overflow-x-auto">
        {[
          { key: "detail", label: "Détails" },
          { key: "devis", label: `Devis${prestation.devis.length > 0 ? ` (${prestation.devis.length})` : ""}` },
          { key: "messages", label: `Messages${prestation.messages.length > 0 ? ` (${prestation.messages.length})` : ""}` },
          ...(canPlanifier ? [{ key: "planifier", label: "🗓 Planifier" }] : []),
          ...(canRapport ? [{ key: "rapport", label: "📋 Rapport" }] : []),
          ...(canFacture || prestation.factureUrl ? [{ key: "facturation", label: "🧾 Facturation" }] : []),
          ...(canAnnuler ? [{ key: "annuler", label: "❌ Annuler" }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2.5 text-sm border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* ── DETAIL ── */}
        {tab === "detail" && (
          <div className="space-y-5">
          {/* Frise chronologique */}
          {(() => {
            const d = prestation.devis?.[0] || null;
            type Step = { label: string; date: string | null; done: boolean; refused?: boolean };
            const WORKFLOW_STEPS: Array<Omit<Step, "done">> = [
              { label: "Demande reçue", date: prestation.createdAt, refused: false },
              { label: "Devis envoyé", date: d?.envoyeAt || null, refused: false },
              { label: d?.accepte === false ? "Devis refusé" : "Devis accepté", date: d?.accepteAt || null, refused: d?.accepte === false },
              { label: "Planifiée", date: prestation.dateIntervention || null, refused: false },
              { label: "Réalisée", date: null, refused: false },
              { label: "Clôturée", date: null, refused: false },
            ];
            const ORDER = ["demande_recue","devis_envoye","devis_accepte","devis_refuse","intervention_planifiee","intervention_realisee","facture","clos","annule"];
            const currentIdx = ORDER.indexOf(prestation.statut);
            const steps: Step[] = WORKFLOW_STEPS.map((s, i) => ({
              ...s, done: i === 0 || (i === 1 && currentIdx >= 1) || (i === 2 && currentIdx >= 2) || (i === 3 && currentIdx >= 4) || (i === 4 && currentIdx >= 5) || (i === 5 && currentIdx >= 7),
            }));
            return (
              <div className="bg-card border border-border/50 rounded-xl p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Historique du workflow</p>
                <div className="flex items-start gap-0">
                  {steps.map((step, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center relative">
                      {i < steps.length - 1 && (
                        <div className={`absolute top-3 left-1/2 w-full h-0.5 ${step.done && steps[i+1]?.done ? "bg-green-500" : "bg-border/50"}`} style={{ zIndex: 0 }} />
                      )}
                      <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs border-2 shrink-0 transition-colors ${step.done ? step.refused ? "bg-red-500 border-red-500 text-white" : "bg-green-500 border-green-500 text-white" : "bg-background border-border text-muted-foreground"}`}>
                        {step.done ? (step.refused ? "✕" : "✓") : <span className="w-1.5 h-1.5 rounded-full bg-border block" />}
                      </div>
                      <div className="mt-2 text-center px-1">
                        <p className={`text-xs font-medium leading-tight ${step.done ? step.refused ? "text-red-600 dark:text-red-400" : "text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                        {step.date && <p className="text-xs text-muted-foreground mt-0.5">{formatDate(step.date)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Notation revendeur */}
          {prestation.notation && (
            <div className="bg-card border border-border/50 rounded-xl p-4 flex items-start gap-3">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <StarDisplay value={prestation.notation} />
                  <span className="text-xs text-muted-foreground">Notation partenaire — {formatDate(prestation.notationAt)}</span>
                </div>
                {prestation.notationCommentaire && <p className="text-sm text-foreground italic">"{prestation.notationCommentaire}"</p>}
              </div>
            </div>
          )}

          {/* Annulation display */}
          {prestation.statut === "annule" && prestation.motifAnnulation && (
            <div className="bg-secondary border border-border/50 rounded-xl p-4 text-sm text-muted-foreground flex items-start gap-2">
              <Ban className="w-4 h-4 shrink-0 mt-0.5 text-destructive" />
              <div><p className="font-medium text-foreground">Prestation annulée</p><p className="mt-0.5">Motif : <em>{prestation.motifAnnulation}</em></p></div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Infos prestation */}
            <div className="space-y-4">
              <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Informations prestation</h3>
                <div className="space-y-2.5 text-sm">
                  <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Type</span><span className="font-medium">{prestation.typePrestation}</span></div>
                  {prestation.niveauUrgence && (
                    <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Urgence</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCE_COLORS[prestation.niveauUrgence] || ""}`}>{URGENCE_LABELS[prestation.niveauUrgence]}</span>
                    </div>
                  )}
                  {prestation.siteNom && <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Site</span><span>{prestation.siteNom}{prestation.siteAdresse ? ` — ${prestation.siteAdresse}` : ""}</span></div>}
                  {prestation.clientFinalNom && <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Client final</span><span>{prestation.clientFinalNom}</span></div>}
                  {prestation.dateIntervention && (
                    <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Date planifiée</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(prestation.dateIntervention)}{prestation.dateInterventionFin ? ` → ${formatDate(prestation.dateInterventionFin)}` : ""}</span>
                    </div>
                  )}
                  {prestation.missionId && (
                    <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Mission liée</span>
                      <a href={`/missions/${prestation.missionId}`} className="text-primary hover:underline text-xs font-mono">{prestation.missionId.slice(0, 8)}…</a>
                    </div>
                  )}
                  {prestation.ticketId && (
                    <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Ticket SAV</span>
                      <div className="flex items-center gap-2">
                        <a href={`/tickets`} className="flex items-center gap-1 text-primary hover:underline text-xs">
                          <TicketCheck className="w-3.5 h-3.5" />
                          {tickets?.find(t => t.id === prestation.ticketId)?.ticketNumber || prestation.ticketId.slice(0, 8) + "…"}
                        </a>
                        <button onClick={() => { setSelectedTicketId(""); setShowLierTicket(true); }} className="text-xs text-muted-foreground hover:text-destructive">×</button>
                      </div>
                    </div>
                  )}
                  {prestation.factureNumeroZoho && (
                    <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Facture</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{prestation.factureNumeroZoho}</span>
                        {prestation.factureMontant && <span className="text-muted-foreground text-xs">({Number(prestation.factureMontant).toFixed(2)} € HT)</span>}
                        {prestation.factureUrl && (
                          <button onClick={handleDownloadFacture} className="text-primary hover:underline text-xs flex items-center gap-1">
                            <Download className="w-3 h-3" /> PDF
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0">Créée le</span><span>{formatDate(prestation.createdAt)}</span></div>
                </div>

                {prestation.contactTerrainNom && (
                  <div className="border-t border-border/40 pt-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Contact terrain</p>
                    <div className="flex items-center gap-2 text-sm"><User className="w-3.5 h-3.5 text-muted-foreground" />{prestation.contactTerrainNom}</div>
                    {prestation.contactTerrainTel && <div className="flex items-center gap-2 text-sm"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{prestation.contactTerrainTel}</div>}
                    {prestation.contactTerrainEmail && <div className="flex items-center gap-2 text-sm"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{prestation.contactTerrainEmail}</div>}
                  </div>
                )}

                {prestation.modeleRobot && (
                  <div className="border-t border-border/40 pt-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Robot concerné</p>
                    <div className="flex items-center gap-2 text-sm"><Briefcase className="w-3.5 h-3.5 text-muted-foreground" />{prestation.modeleRobot}{prestation.numeroSerie ? ` — S/N ${prestation.numeroSerie}` : ""}</div>
                  </div>
                )}

                {prestation.descriptionPanne && (
                  <div className="border-t border-border/40 pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Description de la panne</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{prestation.descriptionPanne}</p>
                  </div>
                )}

                {prestation.piecesSuspectes?.length > 0 && (
                  <div className="pt-1 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Pièces suspectes</p>
                    <div className="flex flex-wrap gap-1.5">
                      {prestation.piecesSuspectes.map(p => (
                        <span key={p} className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 rounded-full">{p}</span>
                      ))}
                    </div>
                  </div>
                )}

                {prestation.description && (
                  <div className="border-t border-border/40 pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Note</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{prestation.description}</p>
                  </div>
                )}
              </div>

              {prestation.piecesJointesSignees?.length > 0 && (
                <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Pièces jointes</h3>
                  {prestation.piecesJointesSignees.map((pj, i) => (
                    <a key={i} href={pj.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline">
                      <FileText className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{pj.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">({Math.round(pj.size / 1024)} Ko)</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Gestion interne */}
            <div className="space-y-4">
              <div className="bg-card border border-border/50 rounded-xl p-4 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Gestion interne</h3>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Statut</Label>
                  <Select defaultValue={prestation.statut} onValueChange={v => patchMutation.mutate({ statut: v })}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-statut">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUTS.map(s => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Note interne (non visible par le partenaire)</Label>
                  <Textarea className="text-sm resize-none" rows={3}
                    defaultValue={prestation.noteInterne || ""}
                    onBlur={e => { if (e.target.value !== (prestation.noteInterne || "")) patchMutation.mutate({ noteInterne: e.target.value }); }}
                    data-testid="input-note-interne" />
                </div>

                {/* Liaison ticket SAV */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Ticket SAV lié</Label>
                    <button onClick={() => { setSelectedTicketId(prestation.ticketId || ""); setShowLierTicket(!showLierTicket); }}
                      className="text-xs text-primary hover:underline flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> {prestation.ticketId ? "Changer" : "Lier un ticket"}
                    </button>
                  </div>
                  {showLierTicket && (
                    <div className="flex gap-2">
                      <Select value={selectedTicketId} onValueChange={setSelectedTicketId}>
                        <SelectTrigger className="h-8 text-sm flex-1" data-testid="select-ticket">
                          <SelectValue placeholder="— Aucun ticket —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">— Aucun —</SelectItem>
                          {(tickets || []).map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.ticketNumber}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={handleLierTicket} disabled={lierTicketLoading} data-testid="btn-lier-ticket">
                        {lierTicketLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "OK"}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Shortcut CTA */}
                {canPlanifier && (
                  <Button size="sm" className="w-full gap-2" onClick={() => setTab("planifier")} data-testid="btn-planifier-shortcut">
                    <Calendar className="w-3.5 h-3.5" /> Planifier l'intervention
                  </Button>
                )}
                {canRapport && (
                  <Button size="sm" variant="outline" className="w-full gap-2" onClick={() => setTab("rapport")} data-testid="btn-rapport-shortcut">
                    <ClipboardList className="w-3.5 h-3.5" /> Déposer le rapport
                  </Button>
                )}
                {canFacture && (
                  <Button size="sm" variant="outline" className="w-full gap-2" onClick={() => setTab("facturation")} data-testid="btn-facturation-shortcut">
                    <Receipt className="w-3.5 h-3.5" /> Émettre la facture
                  </Button>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* ── PLANIFIER ── */}
        {tab === "planifier" && (
          <div className="max-w-xl space-y-5">
            <div>
              <h3 className="text-base font-semibold text-foreground">Planifier l'intervention</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Une mission sera automatiquement créée dans le module Missions et le partenaire sera notifié.</p>
            </div>
            {planError && <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg"><AlertTriangle className="w-4 h-4 shrink-0" /> {planError}</div>}
            <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Date de début *</Label>
                  <Input type="datetime-local" value={planDateDebut} onChange={e => setPlanDateDebut(e.target.value)} data-testid="input-plan-debut" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Date de fin</Label>
                  <Input type="datetime-local" value={planDateFin} onChange={e => setPlanDateFin(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Technicien assigné</Label>
                <Select value={planTechnicien} onValueChange={setPlanTechnicien}>
                  <SelectTrigger data-testid="select-technicien"><SelectValue placeholder="— Sélectionner un technicien —" /></SelectTrigger>
                  <SelectContent>
                    {(technicians || [])
                      .filter(t => t.isActive !== false)
                      .map(t => <SelectItem key={t.id} value={t.id}>{t.prenom} {t.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Note d'intervention (visible partenaire)</Label>
                <Textarea className="resize-none text-sm" rows={3} value={planNote} onChange={e => setPlanNote(e.target.value)} placeholder="Informations pratiques, adresse de RDV…" data-testid="input-plan-note" />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setTab("detail")}>Annuler</Button>
                <Button size="sm" onClick={handlePlanifier} disabled={planLoading || !planDateDebut} data-testid="btn-confirm-planifier">
                  {planLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  <Wrench className="w-3.5 h-3.5 mr-1.5" /> Confirmer la planification
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── RAPPORT ── */}
        {tab === "rapport" && (
          <div className="max-w-xl space-y-5">
            <div>
              <h3 className="text-base font-semibold text-foreground">Déposer le rapport d'intervention</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Le partenaire sera notifié et la prestation passera en statut "Réalisée".</p>
            </div>
            {rapportError && <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg"><AlertTriangle className="w-4 h-4 shrink-0" /> {rapportError}</div>}
            <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Compte-rendu d'intervention</Label>
                <Textarea className="resize-none text-sm" rows={4} value={rapportCommentaire} onChange={e => setRapportCommentaire(e.target.value)} placeholder="Décrivez les actions réalisées…" data-testid="input-rapport-commentaire" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Documents joints (max 3)</Label>
                <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 transition-colors" data-testid="rapport-drop-zone">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Cliquer pour sélectionner des fichiers</span>
                  <input ref={rapportInputRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx" className="hidden"
                    onChange={e => setRapportFiles(prev => [...prev, ...Array.from(e.target.files || [])].slice(0, 3))} />
                </label>
                {rapportFiles.length > 0 && (
                  <div className="space-y-1.5 mt-2">
                    {rapportFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-secondary/50 rounded-lg text-sm">
                        <span className="truncate flex-1 mr-2 text-foreground">{f.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0 mr-2">{Math.round(f.size / 1024)} Ko</span>
                        <button onClick={() => setRapportFiles(files => files.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive transition-colors text-lg leading-none">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setTab("detail")}>Annuler</Button>
                <Button size="sm" onClick={handleRapport} disabled={rapportLoading || (rapportFiles.length === 0 && !rapportCommentaire.trim())} data-testid="btn-confirm-rapport">
                  {rapportLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Déposer le rapport
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── FACTURATION ── */}
        {tab === "facturation" && (
          <div className="max-w-xl space-y-5">
            <div>
              <h3 className="text-base font-semibold text-foreground">Facturation</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Enregistrez la facture et notifiez le partenaire. La prestation passera en statut "Facturée".</p>
            </div>
            {factureError && <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg"><AlertTriangle className="w-4 h-4 shrink-0" /> {factureError}</div>}

            {/* Facture existante */}
            {prestation.factureUrl && (
              <div className="bg-card border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center gap-3">
                <Receipt className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">Facture enregistrée</p>
                  {prestation.factureNumeroZoho && <p className="text-xs text-muted-foreground">Réf Zoho : {prestation.factureNumeroZoho}</p>}
                  {prestation.factureMontant && <p className="text-xs text-muted-foreground">Montant : {Number(prestation.factureMontant).toFixed(2)} € HT</p>}
                </div>
                <Button size="sm" variant="outline" onClick={handleDownloadFacture} className="gap-1.5 shrink-0">
                  <Download className="w-3.5 h-3.5" /> Télécharger
                </Button>
              </div>
            )}

            <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Numéro Zoho</Label>
                <Input value={factureNumeroZoho} onChange={e => setFactureNumeroZoho(e.target.value)} placeholder="FAC-2026-XXXX" data-testid="input-facture-numero" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Montant HT (€)</Label>
                <Input type="number" value={factureMontant} onChange={e => setFactureMontant(e.target.value)} placeholder="0.00" data-testid="input-facture-montant" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">PDF de la facture</Label>
                <label className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 transition-colors">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{factureFile ? factureFile.name : "Cliquer pour sélectionner un PDF"}</span>
                  <input ref={factureInputRef} type="file" accept="application/pdf" className="hidden"
                    onChange={e => setFactureFile(e.target.files?.[0] || null)} data-testid="input-facture-file" />
                </label>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setTab("detail")}>Retour</Button>
                <Button size="sm" onClick={handleFacture} disabled={factureLoading} data-testid="btn-confirm-facture">
                  {factureLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  <Receipt className="w-3.5 h-3.5 mr-1.5" /> Enregistrer la facture
                </Button>
                {canClore && (
                  <Button size="sm" variant="outline" onClick={() => cloreMutation.mutate()} disabled={cloreMutation.isPending} className="ml-auto">
                    {cloreMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                    Clôturer la prestation
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── ANNULER ── */}
        {tab === "annuler" && (
          <div className="max-w-xl space-y-5">
            <div>
              <h3 className="text-base font-semibold text-foreground text-destructive flex items-center gap-2"><Ban className="w-5 h-5" /> Annuler la prestation</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Cette action est définitive. Le partenaire sera notifié par email.</p>
            </div>
            {annulationError && <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg"><AlertTriangle className="w-4 h-4 shrink-0" /> {annulationError}</div>}
            <div className="bg-card border border-destructive/30 rounded-xl p-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Motif d'annulation <span className="text-destructive">*</span></Label>
                <Textarea className="resize-none text-sm" rows={3} value={motifAnnulation} onChange={e => setMotifAnnulation(e.target.value)} placeholder="Expliquez la raison de l'annulation…" data-testid="input-motif-annulation" />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setTab("detail")}>Retour</Button>
                <Button size="sm" variant="destructive" onClick={handleAnnuler} disabled={annulationLoading || !motifAnnulation.trim()} data-testid="btn-confirm-annulation">
                  {annulationLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  <Ban className="w-3.5 h-3.5 mr-1.5" /> Confirmer l'annulation
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── DEVIS ── */}
        {tab === "devis" && (
          <div className="space-y-4 max-w-3xl">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Historique des devis</h3>
              <div className="flex gap-2">
                {canAvenant && !showAvenantForm && (
                  <Button size="sm" variant="outline" onClick={() => { setShowAvenantForm(true); setShowDevisForm(false); setLignes([{ ...EMPTY_LIGNE }]); }} className="gap-1.5" data-testid="btn-avenant">
                    <GitFork className="w-3.5 h-3.5" /> Avenant
                  </Button>
                )}
                {!showDevisForm && !showAvenantForm && (
                  <Button size="sm" onClick={() => { setShowDevisForm(true); setShowAvenantForm(false); setLignes([{ ...EMPTY_LIGNE }]); }} data-testid="btn-new-devis">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau devis
                  </Button>
                )}
              </div>
            </div>

            {/* Formulaire devis / avenant */}
            {(showDevisForm || showAvenantForm) && (
              <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
                <h4 className="text-sm font-semibold text-foreground">
                  {showAvenantForm ? <span className="flex items-center gap-1.5"><GitFork className="w-4 h-4" /> Créer un avenant (v{(prestation.devis?.[0]?.version || 0) + 1})</span> : "Créer un devis"}
                </h4>
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_60px_90px_80px_28px] gap-2 text-xs text-muted-foreground px-1">
                    <span>Description</span><span className="text-right">Qté</span><span className="text-right">P.U. HT</span><span className="text-right">Total</span><span />
                  </div>
                  {lignes.map((l, i) => (
                    <div key={i} className="grid grid-cols-[1fr_60px_90px_80px_28px] gap-2 items-center">
                      <Input placeholder="Description" value={l.description} onChange={e => updateLigne(i, "description", e.target.value)} className="text-sm h-8" />
                      <Input type="number" value={l.quantite} onChange={e => updateLigne(i, "quantite", e.target.value)} className="text-sm h-8 text-right" />
                      <Input type="number" value={l.prixUnitaire} onChange={e => updateLigne(i, "prixUnitaire", e.target.value)} className="text-sm h-8 text-right" />
                      <span className="text-sm text-right text-foreground font-medium">{l.total.toFixed(2)} €</span>
                      <button onClick={() => setLignes(ls => ls.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive text-lg">×</button>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => setLignes(ls => [...ls, { ...EMPTY_LIGNE }])}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <div className="space-y-1"><Label className="text-xs text-muted-foreground">TVA (%)</Label><Input type="number" value={tauxTva} onChange={e => setTauxTva(e.target.value)} className="text-sm h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs text-muted-foreground">Validité (jours)</Label><Input type="number" value={validiteJours} onChange={e => setValiditeJours(e.target.value)} className="text-sm h-8" /></div>
                  <div className="text-sm font-semibold text-foreground text-right"><p className="text-xs text-muted-foreground font-normal">Total TTC</p>{montantTtc.toFixed(2)} €</div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Conditions de paiement</Label>
                  <Textarea className="text-sm resize-none" rows={2} value={conditions} onChange={e => setConditions(e.target.value)} placeholder="30 jours fin de mois…" />
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setShowDevisForm(false); setShowAvenantForm(false); }}>Annuler</Button>
                  <Button size="sm" onClick={() => {
                    const payload = { lignes, tauxTva: Number(tauxTva), validiteJours: Number(validiteJours), conditions: conditions || null };
                    if (showAvenantForm) avenantMutation.mutate(payload);
                    else devisMutation.mutate(payload);
                  }} disabled={devisMutation.isPending || avenantMutation.isPending} data-testid="btn-save-devis">
                    {(devisMutation.isPending || avenantMutation.isPending) && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                    Enregistrer
                  </Button>
                </div>
              </div>
            )}

            {prestation.devis.length === 0 && !showDevisForm && (
              <div className="text-center py-12 text-sm text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" /> Aucun devis créé.
              </div>
            )}

            {prestation.devis.map(d => (
              <div key={d.id} className="bg-card border border-border/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">Version {d.version}</span>
                    {d.estAvenant && <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">avenant</span>}
                    {d.accepte === true && <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle className="w-3.5 h-3.5" /> Accepté</span>}
                    {d.accepte === false && <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400"><XCircle className="w-3.5 h-3.5" /> Refusé</span>}
                    {d.envoye && d.accepte === null && <span className="text-xs text-amber-600 dark:text-amber-400">En attente de réponse</span>}
                    {!d.envoye && <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">Brouillon</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDate(d.createdAt)}</span>
                </div>
                <div className="text-sm space-y-1.5">
                  {(d.lignes || []).map((l, i) => (
                    <div key={i} className="flex justify-between text-muted-foreground">
                      <span className="flex-1 mr-2">{l.description} × {l.quantite}</span>
                      <span>{Number(l.total).toFixed(2)} €</span>
                    </div>
                  ))}
                  <div className="border-t border-border/40 pt-1.5 flex justify-between font-semibold text-foreground">
                    <span>Total TTC ({d.tauxTva}% TVA)</span>
                    <span>{Number(d.montantTtc || 0).toFixed(2)} €</span>
                  </div>
                </div>
                {d.accepteNote && <p className="text-xs text-muted-foreground italic">Note/motif partenaire : "{d.accepteNote}"</p>}
                {d.accepte !== null && d.acceptationIp && (
                  <p className="text-xs text-muted-foreground/60">Audit — IP : {d.acceptationIp} — {formatDate(d.accepteAt)}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── MESSAGES ── */}
        {tab === "messages" && (
          <div className="max-w-3xl space-y-3">
            {prestation.messages.length === 0 && (
              <div className="text-center py-12 text-sm text-muted-foreground">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" /> Aucun message.
              </div>
            )}
            {prestation.messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.auteurType === "internal" ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.id}`}>
                <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm space-y-1 ${msg.auteurType === "internal" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                  <p className="text-xs font-medium opacity-70">{msg.auteur}</p>
                  <p className="whitespace-pre-wrap">{msg.contenu}</p>
                  <p className="text-xs opacity-50">{formatDate(msg.createdAt)}</p>
                </div>
              </div>
            ))}
            <div className="pt-4 border-t border-border/60">
              <div className="flex items-end gap-2">
                <Textarea className="flex-1 resize-none text-sm" rows={2}
                  placeholder="Message visible par le partenaire..."
                  value={msgContent} onChange={e => setMsgContent(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (msgContent.trim()) msgMutation.mutate(msgContent.trim()); } }}
                  data-testid="input-message" />
                <Button size="icon" onClick={() => { if (msgContent.trim()) msgMutation.mutate(msgContent.trim()); }}
                  disabled={msgMutation.isPending || !msgContent.trim()} data-testid="btn-send">
                  {msgMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
